import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconArrowsJoin2, IconChevronLeft, IconChevronRight, IconGripVertical } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import VideoDropZone from '../../components/video/VideoDropZone';
import useVideoPaste from '../../components/video/useVideoPaste';
import { VideoFileRow } from '../../components/video/VideoFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import {
    MAX_MERGE_FILE_BYTES,
    MIN_MERGE_FILES,
    formatFileSize,
    isSupportedVideoFile,
    mergeVideos,
    mergedVideoName,
    preloadFFmpeg,
} from './mergeVideoEngine';
import {
    createToolItemId,
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

function SortableVideoRow({ item, index, onRemove, t }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id });

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.55 : 1,
            }}
        >
            <VideoFileRow
                name={item.file.name}
                meta={formatFileSize(item.file.size)}
                orderBadge={toBnDigits(index + 1)}
                onRemove={() => onRemove(item.id)}
                removeLabel={t('mergeVideo.removeFile')}
                dragHandle={(
                    <button
                        type="button"
                        className="cursor-grab border-none bg-transparent p-0 active:cursor-grabbing"
                        aria-label={t('mergeVideo.reorder')}
                        {...attributes}
                        {...listeners}
                    >
                        <IconGripVertical size={16} />
                    </button>
                )}
            />
        </div>
    );
}

export default function MergeVideo() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [items, setItems] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    useEffect(() => {
        preloadFFmpeg();
    }, []);

    const activeFile = items[previewIndex]?.file ?? null;
    const activePreviewUrl = useMemo(
        () => (activeFile ? URL.createObjectURL(activeFile) : ''),
        [activeFile],
    );

    const resultPreviewUrl = useMemo(
        () => (result ? URL.createObjectURL(result.blob) : ''),
        [result],
    );

    useEffect(() => () => {
        if (activePreviewUrl) URL.revokeObjectURL(activePreviewUrl);
    }, [activePreviewUrl]);

    useEffect(() => () => {
        if (resultPreviewUrl) URL.revokeObjectURL(resultPreviewUrl);
    }, [resultPreviewUrl]);

    useEffect(() => {
        if (previewIndex >= items.length) {
            setPreviewIndex(Math.max(0, items.length - 1));
        }
    }, [items.length, previewIndex]);

    const totalSize = useMemo(
        () => items.reduce((sum, item) => sum + item.file.size, 0),
        [items],
    );

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const videos = list.filter(isSupportedVideoFile);
        if (!videos.length) {
            const msg = t('mergeVideo.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        let tooLarge = false;
        let duplicate = false;
        setItems((prev) => {
            const next = [...prev];
            const existingNames = new Set(next.map((item) => item.file.name));
            for (const file of videos) {
                if (file.size > MAX_MERGE_FILE_BYTES) {
                    tooLarge = true;
                    continue;
                }
                if (existingNames.has(file.name)) {
                    duplicate = true;
                    continue;
                }
                existingNames.add(file.name);
                next.push({ id: createToolItemId(), file });
            }
            return next;
        });

        if (tooLarge) {
            const msg = t('mergeVideo.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } else if (duplicate) {
            notifications.show({
                title: t('common.error'),
                message: t('mergeVideo.errors.duplicate'),
                color: 'yellow',
            });
        } else {
            setError(null);
        }
        setResult(null);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page, the same as the image and PDF
    // tools. Ignored while a run is in flight: the clip would be dropped
    // on the floor by the reset that follows it.
    useVideoPaste(addFiles, { enabled: !processing });

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeItem = (id) => {
        setItems((prev) => prev.filter((item) => item.id !== id));
        setResult(null);
        setError(null);
    };

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setItems((prev) => {
            const oldIndex = prev.findIndex((item) => item.id === active.id);
            const newIndex = prev.findIndex((item) => item.id === over.id);
            return arrayMove(prev, oldIndex, newIndex);
        });
        setResult(null);
    };

    const handleReset = () => {
        setItems([]);
        setResult(null);
        setError(null);
        setProgress(null);
        setPreviewIndex(0);
    };

    const resolveErrorMessage = (err) => {
        const code = err?.message;
        if (code === 'INCOMPATIBLE') return t('mergeVideo.errors.incompatible');
        if (code === 'OUT_OF_MEMORY') return t('mergeVideo.errors.outOfMemory');
        if (code === 'EMPTY_OUTPUT' || code === 'MERGE_FAILED') return t('mergeVideo.errors.failed');
        return t('mergeVideo.errors.failed');
    };

    const handleMerge = async () => {
        if (items.length < MIN_MERGE_FILES || processing) return;

        recordToolUsage('video/merge', { category: 'video' });

        setProcessing(true);
        setResult(null);
        setError(null);
        setProgress(0);

        try {
            const files = items.map((item) => item.file);
            const blob = await mergeVideos(files, (value) => setProgress(value));
            setResult({
                name: mergedVideoName(),
                size: blob.size,
                fileCount: files.length,
                blob,
            });
            notifications.show({
                title: t('common.success'),
                message: t('mergeVideo.done'),
                color: 'green',
            });
        } catch (err) {
            const msg = resolveErrorMessage(err);
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const status = deriveToolStatus({
        hasInput: items.length > 0,
        canRun: items.length >= MIN_MERGE_FILES,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`mergeVideo.howTo.step${i}`)),
        [t],
    );

    const statusText = getToolStatusText(status, t, {
        empty: t('mergeVideo.previewEmpty'),
        ready: t('mergeVideo.previewReady', { count: toBnDigits(items.length) }),
        success: t('mergeVideo.done'),
    });

    const successLabel = result
        ? t('mergeVideo.resultMeta', {
            files: toBnDigits(result.fileCount),
            size: formatFileSize(result.size),
        })
        : '';

    return (
        <ToolPageLayout
            slug="video/merge"
            subtitleKey="mergeVideo.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(items.length) })}
            showAdd={items.length > 0}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={items.length > 0 || result || error ? handleReset : undefined}
            primaryAction={{
                label: t('mergeVideo.merge'),
                onClick: handleMerge,
                disabled: items.length < MIN_MERGE_FILES || processing || !!result,
                loading: processing,
                icon: <IconArrowsJoin2 size={18} stroke={1.9} />,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*,.mp4,.mov,.avi,.mkv,.webm,.m4v"
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    {items.length === 0 ? (
                        <VideoDropZone
                            title={t('mergeVideo.dropTitle')}
                            hint={t('mergeVideo.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <div className="space-y-2">
                            <div className="max-h-[220px] space-y-2 overflow-y-auto pr-1 [scrollbar-width:thin]">
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                                        {items.map((item, index) => (
                                            <SortableVideoRow
                                                key={item.id}
                                                item={item}
                                                index={index}
                                                onRemove={removeItem}
                                                t={t}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            </div>
                            <p className="flex items-center gap-1.5 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                <IconGripVertical size={13} stroke={1.8} />
                                {t('mergeVideo.orderHint')}
                            </p>
                            {items.length > 0 ? (
                                <p className="text-[11.5px] font-medium text-[#6B635C] dark:text-zinc-400">
                                    {t('mergeVideo.fileCount', {
                                        count: toBnDigits(items.length),
                                        size: formatFileSize(totalSize),
                                    })}
                                </p>
                            ) : null}
                        </div>
                    )}

                    {items.length === 1 ? (
                        <p className="text-xs text-violet-600 dark:text-violet-400">{t('mergeVideo.needMore')}</p>
                    ) : null}
                </>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('mergeVideo.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('mergeVideo.previewEmpty')}
                    idleHint={t('mergeVideo.previewEmptyHint')}
                    processingLabel={t('mergeVideo.merging')}
                    progress={progress ?? undefined}
                    error={error}
                    successLabel={successLabel}
                    downloadLabel={t('mergeVideo.download')}
                    onDownload={result ? () => downloadBlob(result.blob, result.name) : undefined}
                    onAgain={result ? handleReset : undefined}
                    againLabel={t('mergeVideo.startOver')}
                    readyHint={status === 'ready' && items.length >= MIN_MERGE_FILES
                        ? <ToolReadyHint actionLabel={t('mergeVideo.merge')} />
                        : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={items.length === 0 && !error}
                >
                    {(result || items.length > 0) && !processing && !error ? (
                        <div className="flex h-full min-h-[280px] flex-col">
                            <div className="flex flex-1 items-center justify-center overflow-hidden rounded-xl border border-[#ECE8E4] bg-black dark:border-zinc-700">
                                <video
                                    src={result ? resultPreviewUrl : activePreviewUrl}
                                    controls
                                    className="max-h-[360px] w-full max-w-full"
                                />
                            </div>
                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                <span className="truncate font-medium text-[#3D3833] dark:text-zinc-300">
                                    {result
                                        ? result.name
                                        : items[previewIndex]?.file.name}
                                </span>
                                {!result && items.length > 1 ? (
                                    <div className="flex items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
                                            disabled={previewIndex === 0}
                                            className="flex h-7 w-7 items-center justify-center rounded-md bg-[#F7F5F3] text-[#6B635C] disabled:opacity-40 dark:bg-zinc-800 dark:text-zinc-400"
                                            aria-label={t('mergeVideo.prevPreview')}
                                        >
                                            <IconChevronLeft size={14} />
                                        </button>
                                        <span>
                                            {toBnDigits(previewIndex + 1)} / {toBnDigits(items.length)}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setPreviewIndex((i) => Math.min(items.length - 1, i + 1))}
                                            disabled={previewIndex >= items.length - 1}
                                            className="flex h-7 w-7 items-center justify-center rounded-md bg-[#F7F5F3] text-[#6B635C] disabled:opacity-40 dark:bg-zinc-800 dark:text-zinc-400"
                                            aria-label={t('mergeVideo.nextPreview')}
                                        >
                                            <IconChevronRight size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <span>
                                        {result
                                            ? formatFileSize(result.size)
                                            : formatFileSize(items[previewIndex]?.file.size || 0)}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
